/** The recap's requests, and the hooks the page and the admin board read them through. */

import { useQuery } from "@tanstack/react-query";

import { Forbidden, ServerError, Unauthorized, requestJson, type RequestFailures } from "../api/client";
import type { Available, WrappedAdmin, WrappedResponse } from "./types";

/** A 404: nothing built (admins) or nothing released (everyone else) yet. */
export class NotOut extends Error {}

const WRAPPED: RequestFailures = {
  offline: "couldn't reach the server",
  fail(status, detail) {
    if (status === 401) return new Unauthorized(detail);
    if (status === 403) return new Forbidden(detail);
    if (status === 404) return new NotOut(detail);
    if (status >= 500) return new ServerError(detail, status);
    return new Error(detail);
  },
};

const request = <T>(path: string, init?: RequestInit) => requestJson<T>(path, init, WRAPPED);

const sending = (method: string, body?: unknown): RequestInit => ({
  method,
  headers: body === undefined ? undefined : { "Content-Type": "application/json" },
  body: body === undefined ? undefined : JSON.stringify(body),
});

export const getWrapped = (person?: string) =>
  request<WrappedResponse>(person ? `/api/wrapped?person=${encodeURIComponent(person)}` : "/api/wrapped");
export const getAvailable = () => request<Available>("/api/wrapped/available");
export const getWrappedAdmin = () => request<WrappedAdmin>("/api/admin/wrapped");
export const putWrappedSettings = (settings: { end: string; title: string }) =>
  request<{ end: string; title: string }>("/api/admin/wrapped", sending("PUT", settings));
export const buildWrapped = () => request<{ state: string }>("/api/admin/wrapped/build", sending("POST"));
/** The story's background song: one file, replaced by the next upload. */
export const uploadMusic = (file: File) => {
  const form = new FormData();
  form.append("file", file);
  return request<{ name: string; size: number }>("/api/admin/wrapped/music", { method: "PUT", body: form });
};
export const removeMusic = () => request<void>("/api/admin/wrapped/music", { method: "DELETE" });
export const releaseWrapped = (released: boolean) =>
  request<{ released: boolean }>("/api/admin/wrapped/release", sending("POST", { released }));

export const useWrapped = (person?: string) =>
  useQuery({ queryKey: ["wrapped", person ?? null], queryFn: () => getWrapped(person), staleTime: Infinity });

export const useAvailable = (enabled = true) =>
  useQuery({ queryKey: ["wrapped-available"], queryFn: getAvailable, enabled, staleTime: 60_000, retry: false });

export const useWrappedAdmin = () =>
  useQuery({
    queryKey: ["wrapped-admin"],
    queryFn: getWrappedAdmin,
    // Poll only while a build runs, so the board notices when it finishes.
    refetchInterval: (query) => (query.state.data?.build.state === "running" ? 2000 : false),
  });
